import { useState } from "react";
import { BUSINESS_TYPES, MOMO_PROVIDERS, AVATARS, THEME } from "../utils/helpers";
import { uid } from "../utils/helpers";

const btnStyle = (c = THEME.gold) => ({
  width: "100%", padding: "16px", background: c, color: "#fff",
  border: "none", borderRadius: 16, fontSize: 16, fontWeight: 800,
  cursor: "pointer", boxShadow: `0 4px 15px ${c}44`, transition: "all 0.2s ease",
});

const inputStyle = {
  width: "100%", padding: "14px 16px", borderRadius: 14,
  border: `2px solid ${THEME.border}`, fontSize: 15,
  fontFamily: THEME.font, outline: "none", background: "#fff", boxSizing: "border-box",
};

const title = { fontSize: 24, fontWeight: 900, color: THEME.text, marginBottom: 16 };

export default function Onboarding({ onComplete }) {
  const [step, setStep] = useState(0);
  const [cfg, setCfg] = useState({
    businessName: "", businessType: "", ownerName: "", pin: "",
    employees: [], momoProvider: "", momoNumber: "",
  });
  const [empName, setEmpName] = useState("");
  const [empRole, setEmpRole] = useState("");

  const biz = BUSINESS_TYPES.find((b) => b.key === cfg.businessType);

  const addEmp = () => {
    if (!empName.trim()) return;
    setCfg((c) => ({
      ...c,
      employees: [...c.employees, {
        id: uid(), name: empName.trim(),
        role: empRole.trim() || "Employé",
        avatar: AVATARS[c.employees.length % AVATARS.length],
        active: true,
      }],
    }));
    setEmpName(""); setEmpRole("");
  };

  const color = biz?.color || THEME.gold;

  const steps = [
    // Welcome
    <div key="w" style={{ padding: "0 24px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "85vh" }}>
      <div style={{ fontSize: 80, marginBottom: 20, animation: "float 3s ease-in-out infinite" }}>💰</div>
      <h1 style={{ fontSize: 30, fontWeight: 900, color: THEME.text }}>MonArgent Pro</h1>
      <p style={{ color: THEME.textSub, fontSize: 16, lineHeight: 1.7, margin: "12px 0 0", maxWidth: 300 }}>
        L'application la plus simple pour gérer votre business au quotidien
      </p>
      <div style={{ display: "flex", gap: 8, marginTop: 32, flexWrap: "wrap", justifyContent: "center" }}>
        {["📊 Suivi ventes", "👥 Employés", "💸 Mobile Money", "🔔 Alertes"].map((t, i) => (
          <div key={i} style={{
            background: `${THEME.gold}15`, border: `1px solid ${THEME.gold}33`,
            borderRadius: 20, padding: "6px 14px", fontSize: 12, fontWeight: 700, color: THEME.gold,
            animation: `fadeUp 0.5s ease ${0.3 + i * 0.1}s both`,
          }}>{t}</div>
        ))}
      </div>
      <button onClick={() => setStep(1)} style={{ ...btnStyle(), marginTop: 40, maxWidth: 300 }}>C'est parti ! 🚀</button>
    </div>,

    // Business type
    <div key="b" style={{ padding: "24px 20px" }}>
      <Dots total={5} current={0} color={color} />
      <h2 style={title}>Votre activité</h2>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        {BUSINESS_TYPES.map((b) => (
          <button key={b.key} onClick={() => setCfg((c) => ({ ...c, businessType: b.key }))} style={{
            padding: "16px 12px", borderRadius: 16, textAlign: "left", fontFamily: THEME.font,
            border: cfg.businessType === b.key ? `2.5px solid ${b.color}` : `2px solid ${THEME.border}`,
            background: cfg.businessType === b.key ? `${b.color}10` : "#fff",
            cursor: "pointer", transition: "all 0.2s ease",
            transform: cfg.businessType === b.key ? "scale(1.03)" : "scale(1)",
          }}>
            <div style={{ fontSize: 28 }}>{b.icon}</div>
            <div style={{ fontSize: 13, fontWeight: 700, marginTop: 6 }}>{b.label}</div>
          </button>
        ))}
      </div>
      <input placeholder="Nom de votre business" value={cfg.businessName}
        onChange={(e) => setCfg((c) => ({ ...c, businessName: e.target.value }))}
        style={{ ...inputStyle, marginTop: 16 }}
      />
      <button onClick={() => cfg.businessType && cfg.businessName && setStep(2)}
        disabled={!cfg.businessType || !cfg.businessName}
        style={{ ...btnStyle(color), marginTop: 20, opacity: cfg.businessType && cfg.businessName ? 1 : 0.35 }}>
        Suivant →
      </button>
    </div>,

    // Owner + PIN
    <div key="o" style={{ padding: "24px 20px" }}>
      <Dots total={5} current={1} color={color} />
      <h2 style={title}>Le patron, c'est vous ! {biz?.icon}</h2>
      <input placeholder="Votre nom complet" value={cfg.ownerName}
        onChange={(e) => setCfg((c) => ({ ...c, ownerName: e.target.value }))}
        style={inputStyle}
      />
      <div style={{ marginTop: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>🔒 Code secret (4 chiffres)</div>
        <p style={{ fontSize: 12, color: THEME.textSub, marginBottom: 12 }}>Ce code protège votre tableau de bord.</p>
        <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
          {[0,1,2,3].map((i) => (
            <div key={i} style={{
              width: 52, height: 60, borderRadius: 14,
              border: `2.5px solid ${cfg.pin.length > i ? color : THEME.border}`,
              background: cfg.pin.length > i ? `${color}10` : "#fff",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 28, fontWeight: 900, color, transition: "all 0.2s ease",
            }}>{cfg.pin[i] ? "●" : ""}</div>
          ))}
        </div>
        <input type="tel" maxLength={4} value={cfg.pin}
          onChange={(e) => setCfg((c) => ({ ...c, pin: e.target.value.replace(/\D/g, "").slice(0, 4) }))}
          style={{ ...inputStyle, marginTop: 12, textAlign: "center", letterSpacing: 20, fontSize: 24, fontWeight: 900 }}
        />
      </div>
      <button onClick={() => cfg.ownerName && cfg.pin.length === 4 && setStep(3)}
        disabled={!cfg.ownerName || cfg.pin.length !== 4}
        style={{ ...btnStyle(color), marginTop: 24, opacity: cfg.ownerName && cfg.pin.length === 4 ? 1 : 0.35 }}>
        Suivant →
      </button>
    </div>,

    // Employees
    <div key="e" style={{ padding: "24px 20px" }}>
      <Dots total={5} current={2} color={color} />
      <h2 style={title}>Votre équipe</h2>
      <p style={{ fontSize: 13, color: THEME.textSub, marginBottom: 16, marginTop: -8 }}>
        Ajoutez les personnes qui enregistrent des transactions.
      </p>
      <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
        <input placeholder="Nom" value={empName} onChange={(e) => setEmpName(e.target.value)}
          style={{ ...inputStyle, flex: 1 }} onKeyDown={(e) => e.key === "Enter" && addEmp()} />
        <input placeholder="Rôle" value={empRole} onChange={(e) => setEmpRole(e.target.value)}
          style={{ ...inputStyle, flex: 1 }} onKeyDown={(e) => e.key === "Enter" && addEmp()} />
        <button onClick={addEmp} style={{
          width: 50, borderRadius: 14, border: "none", background: THEME.whatsappLight,
          color: "#fff", fontSize: 22, cursor: "pointer", fontWeight: 900, flexShrink: 0,
        }}>+</button>
      </div>
      <div style={{ maxHeight: 200, overflowY: "auto" }}>
        {cfg.employees.map((emp, idx) => (
          <div key={emp.id} style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "10px 12px", background: idx % 2 === 0 ? "#FAFAF8" : "#fff",
            borderRadius: 12, marginBottom: 4,
          }}>
            <span style={{ fontSize: 24 }}>{emp.avatar}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{emp.name}</div>
              <div style={{ fontSize: 11, color: THEME.textSub }}>{emp.role}</div>
            </div>
            <button onClick={() => setCfg((c) => ({ ...c, employees: c.employees.filter((e) => e.id !== emp.id) }))}
              style={{ background: "#FEE2E2", border: "none", borderRadius: 8, padding: "4px 10px", color: THEME.red, fontWeight: 700, cursor: "pointer", fontSize: 13 }}>✕</button>
          </div>
        ))}
        {cfg.employees.length === 0 && (
          <div style={{ textAlign: "center", padding: 24, color: "#ddd" }}>
            <div style={{ fontSize: 36 }}>👥</div>
            <div style={{ fontSize: 13, marginTop: 6 }}>Pas encore d'employé</div>
          </div>
        )}
      </div>
      <button onClick={() => setStep(4)} style={{ ...btnStyle(color), marginTop: 16 }}>
        {cfg.employees.length > 0 ? `Suivant (${cfg.employees.length} employé${cfg.employees.length > 1 ? "s" : ""}) →` : "Continuer seul →"}
      </button>
    </div>,

    // Mobile Money
    <div key="m" style={{ padding: "24px 20px" }}>
      <Dots total={5} current={3} color={color} />
      <h2 style={title}>Mobile Money 📱</h2>
      <p style={{ fontSize: 13, color: THEME.textSub, marginBottom: 16, marginTop: -8 }}>
        Optionnel — pour suivre vos paiements mobiles.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
        {MOMO_PROVIDERS.map((p) => (
          <button key={p.key} onClick={() => setCfg((c) => ({ ...c, momoProvider: c.momoProvider === p.key ? "" : p.key }))}
            style={{
              padding: "14px 8px", borderRadius: 14, textAlign: "center",
              border: cfg.momoProvider === p.key ? `2.5px solid ${p.color}` : `2px solid ${THEME.border}`,
              background: cfg.momoProvider === p.key ? `${p.color}12` : "#fff",
              cursor: "pointer", fontFamily: THEME.font, transition: "all 0.15s ease",
            }}>
            <div style={{ fontSize: 24 }}>{p.icon}</div>
            <div style={{ fontSize: 10, fontWeight: 700, marginTop: 4 }}>{p.label}</div>
          </button>
        ))}
      </div>
      {cfg.momoProvider && (
        <input placeholder="Numéro Mobile Money" value={cfg.momoNumber}
          onChange={(e) => setCfg((c) => ({ ...c, momoNumber: e.target.value }))}
          type="tel" style={{ ...inputStyle, marginTop: 14 }}
        />
      )}
      <button onClick={() => setStep(5)} style={{ ...btnStyle(color), marginTop: 20 }}>
        {cfg.momoProvider ? "Suivant →" : "Passer →"}
      </button>
    </div>,

    // Ready
    <div key="r" style={{ padding: "24px 20px", textAlign: "center" }}>
      <Dots total={5} current={4} color={color} />
      <div style={{
        width: 90, height: 90, borderRadius: 30,
        background: `linear-gradient(135deg, ${color}, ${color}bb)`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 44, margin: "20px auto",
        boxShadow: `0 8px 30px ${color}33`,
        animation: "popIn 0.5s cubic-bezier(0.68, -0.55, 0.27, 1.55)",
      }}>{biz?.icon}</div>
      <h2 style={{ ...title, textAlign: "center" }}>{cfg.businessName}</h2>
      <div style={{ background: "#FAFAF8", borderRadius: 16, padding: 20, textAlign: "left" }}>
        <Row icon="👤" label="Patron" value={cfg.ownerName} />
        <Row icon="📍" label="Type" value={biz?.label} />
        <Row icon="👥" label="Équipe" value={cfg.employees.length > 0 ? cfg.employees.map((e) => e.name).join(", ") : "Mode solo"} />
        {cfg.momoProvider && <Row icon="📱" label="MoMo" value={`${MOMO_PROVIDERS.find((p) => p.key === cfg.momoProvider)?.label} — ${cfg.momoNumber}`} />}
      </div>
      <button onClick={() => onComplete(cfg)} style={{ ...btnStyle(color), marginTop: 24 }}>
        Lancer MonArgent 🚀
      </button>
    </div>,
  ];

  return (
    <div style={{ minHeight: "100vh", background: THEME.bg }}>
      {steps[step]}
    </div>
  );
}

function Dots({ total, current, color }) {
  return (
    <div style={{ display: "flex", gap: 6, marginBottom: 20, justifyContent: "center" }}>
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} style={{
          width: i === current ? 24 : 8, height: 8, borderRadius: 4,
          background: i <= current ? color : "#E8E5E0",
          transition: "all 0.3s ease",
        }} />
      ))}
    </div>
  );
}

function Row({ icon, label, value }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: `1px solid ${THEME.border}` }}>
      <span style={{ fontSize: 18 }}>{icon}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 11, color: THEME.textSub, fontWeight: 600 }}>{label}</div>
        <div style={{ fontSize: 14, fontWeight: 700 }}>{value}</div>
      </div>
    </div>
  );
}
